import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader.js";
import { ThreeMFLoader } from "three/examples/jsm/loaders/3MFLoader.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

const is3mfSrc = (s) => /\.3mf(\?|#|$)/i.test(s || "");
const isGlbSrc = (s) => /\.(glb|gltf)(\?|#|$)/i.test(s || "");

let dracoLoader = null;
const getDraco = () => {
    if (!dracoLoader) {
        dracoLoader = new DRACOLoader();
        dracoLoader.setDecoderPath(`${process.env.PUBLIC_URL || ""}/draco/`);
        dracoLoader.preload();
    }
    return dracoLoader;
};

const fitObject = (group) => {
    group.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(group);
    const sphere = box.getBoundingSphere(new THREE.Sphere());
    const wrapper = new THREE.Group();
    group.position.sub(sphere.center);
    wrapper.add(group);
    wrapper.scale.setScalar(40 / (sphere.radius || 50));
    group.traverse((o) => {
        if (o.isMesh) {
            if (!o.geometry.attributes.normal) o.geometry.computeVertexNormals();
            const mats = Array.isArray(o.material) ? o.material : [o.material];
            mats.forEach((m) => { if (m && m.side !== undefined) m.side = THREE.DoubleSide; });
        }
    });
    return wrapper;
};

const build3mfObject = (buffer) => {
    const group = new ThreeMFLoader().parse(buffer);
    group.rotation.set(-Math.PI / 2, 0, 0); // 3MF is z-up
    return fitObject(group);
};

const buildGlbObject = (buffer) =>
    new Promise((resolve, reject) => {
        const loader = new GLTFLoader();
        loader.setDRACOLoader(getDraco());
        loader.parse(buffer, "", (gltf) => resolve(fitObject(gltf.scene)), reject);
    });

const disposeObject = (obj) => {
    obj.traverse((o) => {
        if (o.isMesh) {
            try { o.geometry.dispose(); } catch {}
            const mats = Array.isArray(o.material) ? o.material : [o.material];
            mats.forEach((m) => { try { m && m.dispose(); } catch {} });
        }
    });
};

/**
 * Fast, lightweight STL viewer.
 *
 * Perf notes:
 *   • only initialises three.js + fetches the STL when the container is in view (IntersectionObserver)
 *   • fetches the file with a ReadableStream so we can show real byte-level progress
 *   • caps devicePixelRatio to 1.5 and uses "high-performance" GPU hint
 *   • parses via STLLoader.parse on the already-downloaded ArrayBuffer (no double fetch)
 */
const STLViewer = ({ src, height = 320, autoRotate = true, background = "transparent", testId }) => {
    const mountRef = useRef(null);
    const [progress, setProgress] = useState(0); // 0..100
    const [ready, setReady] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const mount = mountRef.current;
        if (!mount || !src) return;

        let disposed = false;
        let renderer, controls, mesh, raf, camera;
        let started = false;
        let observer;

        const boot = async () => {
            if (started || disposed) return;
            started = true;

            const width = mount.clientWidth || 320;
            const scene = new THREE.Scene();
            camera = new THREE.PerspectiveCamera(50, width / height, 0.1, 5000);
            camera.position.set(0, 0, 100);

            renderer = new THREE.WebGLRenderer({
                antialias: true,
                alpha: true,
                powerPreference: "high-performance",
            });
            renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
            renderer.setSize(width, height);
            renderer.setClearColor(0x000000, 0);
            mount.appendChild(renderer.domElement);

            scene.add(new THREE.AmbientLight(0xffffff, 0.55));
            const key = new THREE.DirectionalLight(0xffffff, 0.9);
            key.position.set(1, 1, 1);
            scene.add(key);
            const fill = new THREE.DirectionalLight(0xfff1d6, 0.4);
            fill.position.set(-1, 0.5, -1);
            scene.add(fill);

            controls = new OrbitControls(camera, renderer.domElement);
            controls.enableDamping = true;
            controls.dampingFactor = 0.08;
            controls.autoRotate = autoRotate;
            controls.autoRotateSpeed = 1.2;
            controls.minDistance = 20;
            controls.maxDistance = 600;

            const animate = () => {
                raf = requestAnimationFrame(animate);
                controls.update();
                renderer.render(scene, camera);
            };
            animate();

            // Fetch with byte progress so the user sees something happening within ~50ms
            try {
                const resp = await fetch(src, { credentials: "omit", cache: "force-cache" });
                if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
                const total = Number(resp.headers.get("content-length")) || 0;
                let received = 0;
                const chunks = [];
                if (resp.body && resp.body.getReader && total > 0) {
                    const reader = resp.body.getReader();
                    // Read in a loop, updating progress
                    // eslint-disable-next-line no-constant-condition
                    while (true) {
                        const { done, value } = await reader.read();
                        if (done) break;
                        chunks.push(value);
                        received += value.length;
                        if (!disposed) setProgress(Math.min(99, Math.round((received / total) * 100)));
                    }
                } else {
                    // Fallback: no content-length or streaming — just wait for full buffer
                    const buf = await resp.arrayBuffer();
                    chunks.push(new Uint8Array(buf));
                    received = buf.byteLength;
                    if (!disposed) setProgress(80);
                }
                // Merge chunks → ArrayBuffer
                const merged = new Uint8Array(received);
                let offset = 0;
                for (const c of chunks) { merged.set(c, offset); offset += c.length; }
                if (disposed) return;

                // Parse (synchronous but on an already-downloaded buffer — fast)
                if (is3mfSrc(src)) {
                    mesh = build3mfObject(merged.buffer);
                } else if (isGlbSrc(src)) {
                    mesh = await buildGlbObject(merged.buffer);
                    if (disposed) { disposeObject(mesh); return; }
                } else {
                    const loader = new STLLoader();
                    const geometry = loader.parse(merged.buffer);
                    geometry.computeVertexNormals();
                    geometry.center();
                    geometry.computeBoundingSphere();
                    const radius = geometry.boundingSphere?.radius || 50;
                    const scale = 40 / radius;
                    const material = new THREE.MeshStandardMaterial({
                        color: 0xd9c19a,
                        metalness: 0.15,
                        roughness: 0.55,
                    });
                    mesh = new THREE.Mesh(geometry, material);
                    mesh.scale.setScalar(scale);
                }
                scene.add(mesh);
                if (!disposed) {
                    setProgress(100);
                    setReady(true);
                }
            } catch (err) {
                console.warn("3D model load error", err);
                if (!disposed) setError(err?.message || "Failed to load 3D model");
            }
        };

        // Only start work when the container is actually visible on screen.
        if ("IntersectionObserver" in window) {
            observer = new IntersectionObserver(
                (entries) => {
                    for (const e of entries) {
                        if (e.isIntersecting) {
                            boot();
                            observer.disconnect();
                            break;
                        }
                    }
                },
                { rootMargin: "120px" }
            );
            observer.observe(mount);
        } else {
            boot();
        }

        const onResize = () => {
            if (!mount || !renderer || !camera) return;
            const w = mount.clientWidth;
            camera.aspect = w / height;
            camera.updateProjectionMatrix();
            renderer.setSize(w, height);
        };
        window.addEventListener("resize", onResize);

        return () => {
            disposed = true;
            if (observer) observer.disconnect();
            if (raf) cancelAnimationFrame(raf);
            window.removeEventListener("resize", onResize);
            try { controls && controls.dispose(); } catch {}
            if (mesh) disposeObject(mesh);
            if (renderer) {
                try { renderer.dispose(); } catch {}
                if (renderer.domElement && renderer.domElement.parentNode === mount) {
                    mount.removeChild(renderer.domElement);
                }
            }
        };
    }, [src, height, autoRotate]);

    return (
        <div
            ref={mountRef}
            data-testid={testId}
            style={{ width: "100%", height, background, touchAction: "none", position: "relative" }}
        >
            {!ready && !error && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 pointer-events-none" data-testid={testId ? `${testId}-loading` : undefined}>
                    <div className="w-8 h-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Loading 3D preview · {progress}%</div>
                </div>
            )}
            {error && (
                <div className="absolute inset-0 flex items-center justify-center text-[11px] text-destructive px-4 text-center" data-testid={testId ? `${testId}-error` : undefined}>
                    Couldn't load model — {error}
                </div>
            )}
        </div>
    );
};

export default STLViewer;
